import { persistComicPageImageUrls } from '../api/comics'
import type { CreationTask } from '../api/creations'
import type { ClientUiPublic } from '../api/platformConfig'
import { getEpisodeSettings } from './comicEpisodeSettings'
import { appendPageCandidates } from './comicPageCandidates'
import { buildPageGenerationSnapshot } from './comicPageGenerationSnapshot'
import { getProjectPanelAspect } from './comicProjectSettings'
import { setProjectCoverIfEmpty, updatePageInEpisode } from './comicProjectMutations'
import type { ComicPage, ComicProject } from './comicTypes'
import {
  recoverCreationRoundTask,
} from './creationTaskLifecycle'
import {
  creationTaskAgeMs,
  DEFAULT_CREATION_STALE_MS,
  isCreationTaskTerminal,
} from './creationTaskRecovery'

const STALE_GENERATION_MS = DEFAULT_CREATION_STALE_MS

export function pageGenerationReconcileKey(episodeId: string, pageId: string): string {
  return `${episodeId}:${pageId}`
}

export type ReconcilePageGenerationOptions = {
  /** 本页正在编辑器内同步轮询，勿按「无 taskId」判中断 */
  skipPageKeys?: ReadonlySet<string>
  /**
   * 仅画册初次加载：无 taskId 的 generating 视为刷新/关闭页面导致的中断。
   * 定时 reconcile 勿开启，避免与「先写 generating、后写 taskId」竞态。
   */
  failGeneratingWithoutTaskId?: boolean
  /** 有 taskId 且仍在生成时，是否等待任务完成（页面重入恢复） */
  waitForInFlight?: boolean
}

export function projectHasInFlightPageGeneration(project: ComicProject): boolean {
  return project.episodes.some((ep) =>
    ep.pages.some((p) => p.pageImageStatus === 'generating'),
  )
}

export function listPagesNeedingGenerationReconcile(
  project: ComicProject,
): { episodeId: string; page: ComicPage }[] {
  const out: { episodeId: string; page: ComicPage }[] = []
  for (const ep of project.episodes) {
    for (const page of ep.pages) {
      if (page.pageImageStatus === 'generating') {
        out.push({ episodeId: ep.id, page })
      }
    }
  }
  return out
}

function taskAgeMs(task: CreationTask): number {
  return creationTaskAgeMs(task, STALE_GENERATION_MS)
}

/**
 * 根据 creation_tasks 终态回填画册页状态（关闭页面、超时、后端话级任务异常时兜底）。
 */
export async function reconcilePageGeneration(
  project: ComicProject,
  episodeId: string,
  page: ComicPage,
  clientUi: ClientUiPublic,
  options?: ReconcilePageGenerationOptions,
): Promise<{ project: ComicProject; changed: boolean; message?: string }> {
  if (page.pageImageStatus !== 'generating') {
    return { project, changed: false }
  }

  const ep = project.episodes.find((e) => e.id === episodeId)
  if (!ep) return { project, changed: false }

  const pageKey = pageGenerationReconcileKey(episodeId, page.id)
  if (options?.skipPageKeys?.has(pageKey)) {
    return { project, changed: false }
  }

  const settings = getEpisodeSettings(project, ep, clientUi) ?? project.settings
  const taskId = page.pageCreationTaskId?.trim()

  if (!taskId) {
    if (!options?.failGeneratingWithoutTaskId) {
      return { project, changed: false }
    }
    const updated = updatePageInEpisode(project, episodeId, page.id, {
      pageImageStatus: 'failed',
      pageErrorMessage: '生成已中断，请重新生成本页',
    })
    return { project: updated, changed: true, message: `${page.title}：生成已中断` }
  }

  const task = await recoverCreationRoundTask(taskId, {
    waitForCompletion: options?.waitForInFlight === true,
    staleMs: STALE_GENERATION_MS,
  })
  if (!task) {
    return { project, changed: false }
  }

  if (!isCreationTaskTerminal(task)) {
    if (taskAgeMs(task) <= STALE_GENERATION_MS) {
      return { project, changed: false }
    }
    const updated = updatePageInEpisode(project, episodeId, page.id, {
      pageImageStatus: 'failed',
      pageErrorMessage: '生成超时，请重新生成本页',
      pageCreationTaskId: taskId,
    })
    return { project: updated, changed: true, message: `${page.title}：生成超时` }
  }

  if (task.status === 'FAILED') {
    const updated = updatePageInEpisode(project, episodeId, page.id, {
      pageImageStatus: 'failed',
      pageErrorMessage: task.errorMessage ?? '整页生图失败',
      pageCreationTaskId: taskId,
    })
    return { project: updated, changed: true, message: `${page.title}：${task.errorMessage ?? '生图失败'}` }
  }

  const rawUrls = (task.result?.imageUrls ?? []).filter(Boolean)
  if (rawUrls.length === 0) {
    const updated = updatePageInEpisode(project, episodeId, page.id, {
      pageImageStatus: 'failed',
      pageErrorMessage: '未返回图片',
      pageCreationTaskId: taskId,
    })
    return { project: updated, changed: true, message: `${page.title}：未返回图片` }
  }

  const urls = await persistComicPageImageUrls(project.id, rawUrls, page.title, { episodeId, pageId: page.id })
  let working = appendPageCandidates(
    project,
    episodeId,
    page.id,
    urls,
    taskId,
    settings.maxCandidatesStored,
    settings.candidatesPerGeneration,
    buildPageGenerationSnapshot(settings, getProjectPanelAspect(project)),
  )
  const appliedUrl =
    working.episodes
      .find((e) => e.id === episodeId)
      ?.pages.find((p) => p.id === page.id)?.pageImageUrl ?? urls[0]
  if (!project.coverUrl && appliedUrl) {
    working = setProjectCoverIfEmpty(working, appliedUrl)
  }
  return {
    project: working,
    changed: true,
    message: `${page.title}：生成已完成，已同步到画布`,
  }
}

export async function reconcileProjectPageGenerations(
  project: ComicProject,
  clientUi: ClientUiPublic,
  options?: ReconcilePageGenerationOptions,
): Promise<{ project: ComicProject; changed: boolean; messages: string[] }> {
  let working = project
  let changed = false
  const messages: string[] = []

  for (const { episodeId, page } of listPagesNeedingGenerationReconcile(working)) {
    const current =
      working.episodes.find((e) => e.id === episodeId)?.pages.find((p) => p.id === page.id) ?? page
    const result = await reconcilePageGeneration(working, episodeId, current, clientUi, options)
    working = result.project
    if (result.changed) {
      changed = true
      if (result.message) messages.push(result.message)
    }
  }

  return { project: working, changed, messages }
}
